import { useState, useEffect, useCallback } from 'react';
import api from './api';

/**
 * Module-level in-memory cache + tiny hook for "load this list once,
 * show instantly on re-visit, refresh in background".
 *
 * USAGE:
 *   const { data, loading, refresh } = useCachedFetch('/clients');
 *
 * - First time you call this anywhere in the app for a given key, it fetches.
 * - Every subsequent mount with the same key returns cached data synchronously
 *   AND triggers a background refresh so the user sees updates after a
 *   successful save elsewhere.
 * - Call `bustCache('/clients')` after a POST/PUT/DELETE to force-refresh
 *   the next consumer. Or call `refresh()` from the returned object.
 *
 * Why not React Query? — Adding 22kB to the bundle for ~50 lines of behaviour
 * I can write inline. This is intentionally tiny.
 */
const _cache = new Map();     // key -> data
const _pending = new Map();   // key -> Promise (dedupes concurrent fetches)
const _subscribers = new Map(); // key -> Set<setData>

function _emit(key, value) {
  const subs = _subscribers.get(key);
  if (subs) subs.forEach(set => set(value));
}

async function _fetchKey(key) {
  if (_pending.has(key)) return _pending.get(key);
  const p = api.get(key).then(r => {
    _cache.set(key, r.data);
    _pending.delete(key);
    _emit(key, r.data);
    return r.data;
  }).catch(e => {
    _pending.delete(key);
    throw e;
  });
  _pending.set(key, p);
  return p;
}

export function useCachedFetch(key, { enabled = true } = {}) {
  const [data, setData] = useState(() => _cache.get(key) || null);
  const [loading, setLoading] = useState(() => !_cache.has(key));

  useEffect(() => {
    if (!enabled || !key) return;
    // Subscribe to future updates of this key
    if (!_subscribers.has(key)) _subscribers.set(key, new Set());
    _subscribers.get(key).add(setData);
    // Show cached value instantly, then refresh in the background
    if (_cache.has(key)) {
      setData(_cache.get(key));
      setLoading(false);
      _fetchKey(key).catch(() => {});
    } else {
      setLoading(true);
      _fetchKey(key).finally(() => setLoading(false));
    }
    return () => {
      _subscribers.get(key)?.delete(setData);
    };
  }, [key, enabled]);

  const refresh = useCallback(() => _fetchKey(key), [key]);
  return { data, loading, refresh };
}

/** Bust the cache for one key (call after creating/updating/deleting a row). */
export function bustCache(...keys) {
  for (const key of keys) {
    _cache.delete(key);
    _pending.delete(key);
  }
}

/** Wipe everything (e.g. on logout). */
export function clearAllCaches() {
  _cache.clear();
  _pending.clear();
}
